import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, EmptyState, Notice, PageHeader, Row, Segmented, Stat, StatGrid, Table } from "@plataforma/design-system";
import type { Invoice } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { monthEnd, monthStart, todayIso } from "../../lib/format";
import { invoiceStatusLabel, invoiceStatusTone } from "../../lib/labels";
import { certificateMessage, useFiscalSettings, useInvoices } from "./api";
import { useInvoiceFlow } from "./useInvoiceFlow";

type Filter = "pending" | "issued" | "all";

export function InvoicesPage() {
  const { format, has } = useAppConfig();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("pending");
  const invoices = useInvoices();
  const fiscal = useFiscalSettings();
  const all = invoices.data ?? [];
  const pending = all.filter((invoice) => invoice.status === "pending");
  const issued = all.filter((invoice) => invoice.status === "issued").sort((a, b) => (b.issuedAt ?? "").localeCompare(a.issuedAt ?? ""));
  const month = todayIso().slice(0, 7);
  const issuedThisMonth = issued.filter((invoice) => invoice.issuedAt?.startsWith(month));
  const total = (items: Invoice[]) => items.reduce((sum, invoice) => sum + invoice.value, 0);
  const rows = filter === "pending" ? pending : filter === "issued" ? issued : [...all].sort((a, b) => b.appointmentDate.localeCompare(a.appointmentDate));
  const flow = useInvoiceFlow(pending);
  const certificate = certificateMessage(fiscal.data, format.date);

  return (
    <>
      <PageHeader
        title="Notas fiscais"
        subtitle="Uma nota por consulta realizada, emitida daqui mesmo."
        actions={has("reports") ? <Button onClick={() => navigate(`/relatorios?tipo=invoices&de=${monthStart()}&ate=${monthEnd()}`)}>Relatório para a contabilidade</Button> : null}
      />
      <StatGrid>
        <Stat label="Aguardando emissão" value={pending.length} hint={pending.length ? format.money(total(pending)) : "nada pendente"} />
        <Stat label="Emitidas neste mês" value={issuedThisMonth.length} />
        <Stat label="Valor emitido neste mês" value={format.money(total(issuedThisMonth))} />
      </StatGrid>
      {certificate ? <div style={{ marginBottom: 14 }}><Notice tone={certificate.blocking ? "danger" : "warning"} action={has("settings") ? <Button size="small" onClick={() => navigate("/configuracoes")}>Abrir Configurações</Button> : null}>{certificate.text}</Notice></div> : null}
      <Row align="between" style={{ marginBottom: 12 }}>
        <Segmented label="Filtrar notas" value={filter} onChange={setFilter} options={[{ value: "pending", label: `Pendentes (${pending.length})` }, { value: "issued", label: "Emitidas" }, { value: "all", label: "Todas" }]} />
        {fiscal.data ? <span className="ds-muted ds-small">Emissor: {fiscal.data.issuer} · {fiscal.data.environment === "production" ? "produção" : "produção restrita"}</span> : null}
      </Row>
      <Card>
        <Table<Invoice>
          items={rows}
          rowKey={(invoice) => invoice.id}
          empty={<EmptyState title={filter === "pending" && !invoices.isPending ? "Tudo em dia" : undefined}>{invoices.isPending ? "Carregando…" : filter === "pending" ? "Nenhuma nota aguardando emissão. Uma pendência aparece quando uma consulta é marcada como realizada." : "Nenhuma nota nesta lista."}</EmptyState>}
          columns={[
            { header: "Cliente / consulta", cell: (invoice) => <><strong>{invoice.clientName}</strong><div className="ds-muted ds-small">{invoice.appointmentType}</div></> },
            { header: "Data da consulta", cell: (invoice) => format.date(invoice.appointmentDate) },
            { header: "Situação", cell: (invoice) => <><Badge tone={invoiceStatusTone[invoice.status]}>{invoiceStatusLabel[invoice.status]}</Badge>{invoice.status === "pending" && invoice.lastError ? <span className="invoice-fail">Falha na última tentativa</span> : null}</> },
            { header: "Número", cell: (invoice) => invoice.number ?? "—" },
            { header: "Valor", numeric: true, cell: (invoice) => format.money(invoice.value) },
            {
              header: "",
              cell: (invoice) =>
                invoice.status === "pending" ? (
                  <Row align="end">
                    <Button size="small" variant="primary" onClick={() => flow.open(invoice, "emit")}>Emitir nota</Button>
                    <Button variant="quiet" onClick={() => flow.open(invoice, "manual")}>Registrar manualmente</Button>
                  </Row>
                ) : (
                  <Row align="end"><Button size="small" onClick={() => flow.open(invoice, "view")}>Ver nota</Button></Row>
                ),
            },
          ]}
        />
      </Card>
      {flow.dialog}
    </>
  );
}
